import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Tv } from "lucide-react";
import PageRoutes from "@/utils/constants";
import { Button } from "@/v2/components/ui/button";
import { useChannel, useCreateChannel, useUpdateChannelPreferences } from "./api";

export function CreateChannelStep() {
  const navigate = useNavigate();
  const { channel_id }: any = useParams();
  const isExistingChannel = channel_id !== "0";

  const { data: channel, isLoading: isChannelLoading } = useChannel(channel_id);
  const createChannel = useCreateChannel();
  const updatePreferences = useUpdateChannelPreferences(channel_id);

  const [channelName, setChannelName] = useState("");

  useEffect(() => {
    const name = channel?.Configuration?.Name;
    if (name) setChannelName(name);
  }, [channel]);

  const isSubmitting = createChannel.isPending || updatePreferences.isPending;
  // Submitting before an existing channel's own data has loaded would carry
  // over empty Teams/Players/Events/All/Seasons (all default to [] below),
  // wiping the channel's existing configuration instead of preserving it.
  const isWaitingOnExistingChannel = isExistingChannel && isChannelLoading;

  async function handleSubmit() {
    try {
      if (isExistingChannel) {
        await updatePreferences.mutateAsync({
          Name: channelName,
          Teams: channel?.Configuration?.Teams ?? [],
          Players: channel?.Configuration?.Players ?? [],
          Events: channel?.Configuration?.Events ?? [],
          All: channel?.Configuration?.All ?? [],
          Seasons: channel?.Configuration?.Seasons ?? [],
        });
        navigate(PageRoutes.MyNAHLTV.replace(":id", "2").replace(":channel_id", channel_id));
      } else {
        const created = await createChannel.mutateAsync({
          Name: channelName,
          Teams: [],
          Players: [],
          Events: [],
          All: [],
          Seasons: [],
        });
        navigate(
          PageRoutes.MyNAHLTV.replace(":id", "2").replace(":channel_id", String((created as any)?.ID))
        );
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? "Couldn't build your Spotlight. Please try again.",
      );
    }
  }

  return (
    <div className="flex justify-center" style={{ paddingTop: "48px", paddingBottom: "48px" }}>
      {/* text-left overrides the v1 wizard shell's `<section className="text-center">`
          ancestor, the same way SelectPreferencesStep does: text-align inherits. */}
      <div className="w-full max-w-lg text-left flex flex-col gap-4">
        <div>
          {/* A real count, not decoration: the wizard is two steps, and step 2
              opens on a far denser screen than this one. */}
          <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
            Step 1 of 2
          </div>
          <h1
            className="font-extrabold text-slate-900"
            style={{ fontSize: "26px", lineHeight: 1.2, marginTop: "2px" }}
          >
            Name your Spotlight
          </h1>
          <p className="text-sm text-slate-500" style={{ marginTop: "4px" }}>
            Rename it any time. Next you'll pick the teams and players it follows.
          </p>
        </div>

        <div
          className="rounded-xl border border-slate-200 bg-card flex flex-col gap-4"
          style={{ padding: "20px" }}
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="channelName" className="text-[13px] font-bold text-slate-900">
              Spotlight name
            </label>
            {/* The border lives on this wrapper, not on the input: v2.css's
                unlayered `border-width: 0` preflight replacement outranks any
                border utility on an input, which is why the search and auth
                fields wrap theirs too. Focus shows here, so the input drops
                the global outline (same exclusion, in v2.css). */}
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white h-11 px-3 transition-shadow focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/25">
              <Tv className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
              <input
                id="channelName"
                className="v2-channel-name-input w-full bg-transparent text-base text-slate-900 placeholder:text-slate-400"
                value={channelName}
                onChange={(e) => setChannelName(e.target.value)}
                placeholder="Jordan's highlights"
                autoComplete="off"
              />
            </div>
            <p className="text-xs text-slate-500">
              Shown on your Spotlight and to anyone you share it with.
            </p>
          </div>

          <div
            className="flex justify-end border-t border-slate-100"
            style={{ paddingTop: "16px" }}
          >
            <Button
              onClick={handleSubmit}
              disabled={!channelName || isSubmitting || isWaitingOnExistingChannel}
              className="gap-1.5"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              ) : null}
              Next
              {isSubmitting ? null : <ArrowRight className="w-4 h-4" aria-hidden="true" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
